import {
  ImgHTMLAttributes,
  startTransition,
  useEffect,
  useRef,
  useState,
  ViewTransition,
} from "react";
import { createPortal } from "react-dom";
import { RemoveScroll } from "react-remove-scroll";

import { useKeyboard } from "~/hook/base/useKeyboard";

enum View {
  THUMBNAIL,
  VIEWER,
}

type ImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "alt" | "className" | "src" | "style"
> &
  Required<Pick<ImgHTMLAttributes<HTMLImageElement>, "alt" | "src">> & {
    size: number;
    transitionName: string;
  };

const VIEWER_SIZE_IN_VW = 64;

/** 썸네일 <-> 뷰어 공유 트랜지션에 적용되는 view-transition-class (index.css) */
const IMAGE_TRANSITION_CLASS = "image-viewer";

export const Image = ({
  alt,
  size,
  src,
  transitionName,
  ...props
}: ImageProps) => {
  const backdropRef = useRef<HTMLButtonElement>(null);

  const [view, setView] = useState(View.THUMBNAIL);

  const openViewer = () => {
    startTransition(() => {
      setView(View.VIEWER);
    });
  };

  const closeViewer = () => {
    startTransition(() => {
      setView(View.THUMBNAIL);
    });
  };

  useKeyboard(() => {
    if (view === View.VIEWER) {
      closeViewer();
    }
  }, "Escape");

  useEffect(() => {
    if (view === View.VIEWER) {
      backdropRef.current?.focus();
    }
  }, [view]);

  return (
    <>
      <button onClick={openViewer}>
        {view === View.THUMBNAIL && (
          <ViewTransition
            default="none"
            name={transitionName}
            share={IMAGE_TRANSITION_CLASS}
          >
            <img
              {...props}
              alt={alt}
              className="rounded-full object-cover"
              src={src}
              style={{
                height: size,
                width: size,
              }}
            />
          </ViewTransition>
        )}
        {view === View.VIEWER && (
          <div
            style={{
              height: size,
              width: size,
            }}
          />
        )}
      </button>
      {view === View.VIEWER &&
        createPortal(
          <RemoveScroll>
            <div aria-modal="true" role="dialog">
              <div className="fixed inset-0 z-50 backdrop-blur-md backdrop-brightness-95"></div>
              <button
                className="fixed inset-0 z-50 flex items-center justify-center"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    closeViewer();
                  }
                }}
                ref={backdropRef}
              >
                <ViewTransition
                  default="none"
                  name={transitionName}
                  share={IMAGE_TRANSITION_CLASS}
                >
                  <img
                    {...props}
                    alt={alt}
                    className="rounded-full object-cover"
                    src={src}
                    style={{
                      height: `${String(VIEWER_SIZE_IN_VW)}vw`,
                      width: `${String(VIEWER_SIZE_IN_VW)}vw`,
                    }}
                  />
                </ViewTransition>
              </button>
            </div>
          </RemoveScroll>,
          document.body,
        )}
    </>
  );
};
